import { useMemo } from 'react';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import Tooltip from '@mui/material/Tooltip';
import { downloadExcelSheetFunction } from '../../common/functions/download_excel_sheet..function';

const parseNumber = (value) => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === 'string') {
    const numericString = value.replace(/[^0-9.-]/g, '');
    if (!numericString) {
      return undefined;
    }
    const parsed = Number(numericString);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const formatDateValue = (value) => {
  if (!value) {
    return '--';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '--';
  }
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatCellValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '--';
  }
  return value;
};

const normalizeTenancyValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'own') {
    return 'owned';
  }
  if (normalized === 'iru') {
    return 'leased';
  }
  return normalized;
};

const formatTenancyDisplay = (value) => {
  const normalized = normalizeTenancyValue(value);
  if (normalized === 'owned') {
    return 'Owned';
  }
  if (normalized === 'leased') {
    return 'Leased';
  }
  if (value === null || value === undefined || value === '') {
    return '--';
  }
  return value;
};

const formatLengthValue = (value) => {
  const numeric = parseNumber(value);
  if (numeric === undefined) {
    return '--';
  }
  return `${numeric.toFixed(2)} KM`;
};

export default function InventoryTable({ data = [] }) {
  const cleanedData = useMemo(() => {
    if (!Array.isArray(data)) {
      return [];
    }

    return data.map((item) => {
      const fiberPair = parseNumber(item.fiber_pair ?? item.pair_count);
      const usedPair = parseNumber(item.used_pair);
      const availablePair =
        parseNumber(item.available_pair ?? item.unused_pair) ??
        (fiberPair !== undefined && usedPair !== undefined
          ? Math.max(fiberPair - usedPair, 0)
          : undefined);
      const lengthValue = parseNumber(
        item.length ?? item.link_length ?? item.link_length_km ?? item.distance_km
      );
      const rawTenancy = item.fiber_tenancy_type ?? item.tenancy_type ?? '';

      return {
        layer_name: item.layer_name ?? '--',
        fiber_type: item.fiber_type ?? '--',
        fiber_pair: fiberPair,
        used_pair: usedPair,
        available_pair: availablePair,
        link_rate_gbps: item.link_rate_gbps ?? item.link_rate ?? '--',
        fiber_tenancy_type: formatTenancyDisplay(rawTenancy),
        commission_date: formatDateValue(item.commission_date ?? item.commissioned_at),
        scope_year: item.scope_year ?? item.scopeYear ?? '--',
        region: item.region ?? item.source_region ?? '--',
        city: item.city ?? item.source_city ?? '--',
        ring_unique_name: item.ring_unique_name ?? item.source_ring ?? '--',
        source_site_code: item.source_site_code ?? '--',
        sink_site_code: item.sink_site_code ?? '--',
        link_type: item.link_type ?? '--',
        vendor: item.vendor ?? '--',
        concern_department: item.concern_department ?? 'N/A',
        poc: item.poc ?? 'N/A',
        poc_phone: item.poc_phone ?? 'N/A',
        length: lengthValue,
      };
    });
  }, [data]);

  const columns = useMemo(() => {
    const defaultCell = ({ cell }) => formatCellValue(cell.getValue());

    const baseColumns = [
      { accessorKey: 'layer_name', header: 'Layer Name', size: 160, Cell: defaultCell },
      { accessorKey: 'fiber_type', header: 'Fiber Type', size: 120, Cell: defaultCell },
      { accessorKey: 'region', header: 'Region', size: 120, Cell: defaultCell },
      { accessorKey: 'city', header: 'City', size: 120, Cell: defaultCell },
      { accessorKey: 'ring_unique_name', header: 'Ring', size: 180, Cell: defaultCell },
      {
        accessorKey: 'fiber_tenancy_type',
        header: 'Tenancy Type',
        size: 160,
        Cell: defaultCell,
      },
      {
        accessorKey: 'vendor',
        header: 'Vendor',
        size: 120,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          const displayValue = formatCellValue(value);

          const stylesByVendor = {
            JAZZ: {
              background: '#FEE2E2',
              color: '#B91C1C',
            },
            WATEEN: {
              background: '#DBEAFE',
              color: '#1D4ED8',
            },
            WTL: {
              background: '#FFF7ED',
              color: '#C2410C',
            },
            NAYATEL: {
              background: '#FEF3C7',
              color: '#B45309',
            },
            MPPL: {
              background: '#EDE9FE',
              color: '#6D28D9',
            },
            PTCL: {
              background: '#DCFCE7',
              color: '#047857',
            },
          };

          const defaultStyle = {
            background: '#F3F4F6',
            color: '#111827',
          };

          const vendorKey = String(value ?? '')
            .trim()
            .toUpperCase();
          const style = stylesByVendor[vendorKey] || defaultStyle;

          return (
            <span
              style={{
                background: style.background,
                color: style.color,
                padding: '3px 10px',
                fontSize: '10.5px',
                fontWeight: '550',
                borderRadius: '24px',
                textTransform: 'capitalize',
              }}
            >
              {displayValue}
            </span>
          );
        },
      },
      {
        accessorKey: 'fiber_pair',
        header: 'Fiber Pair',
        size: 100,
        Cell: defaultCell,
      },
      {
        accessorKey: 'used_pair',
        header: 'Used Pair',
        size: 100,
        Cell: defaultCell,
      },
      {
        accessorKey: 'available_pair',
        header: 'Available Pair',
        size: 100,
        Cell: defaultCell,
      },
      {
        accessorKey: 'link_rate_gbps',
        header: 'Link Rate (Gbps)',
        size: 100,
        Cell: defaultCell,
      },
      {
        accessorKey: 'length',
        header: 'Length',
        size: 110,
        Cell: ({ cell }) => formatLengthValue(cell.getValue()),
      },
      {
        accessorKey: 'commission_date',
        header: 'Commission Date',
        size: 150,
        Cell: defaultCell,
      },
      {
        accessorKey: 'scope_year',
        header: 'Scope Year',
        size: 120,
        Cell: defaultCell,
      },
      {
        accessorKey: 'source_site_code',
        header: 'Source Site',
        size: 140,
        Cell: defaultCell,
      },
      {
        accessorKey: 'sink_site_code',
        header: 'Sink Site',
        size: 140,
        Cell: defaultCell,
      },
      {
        accessorKey: 'link_type',
        header: 'Link Type',
        size: 140,
        Cell: defaultCell,
      },
    ];
    return baseColumns.map((column) => ({
      ...column,
      muiTableHeadCellProps: {
        ...(column.muiTableHeadCellProps ?? {}),
        align: 'center',
        sx: {
          ...(column.muiTableHeadCellProps?.sx ?? {}),
          backgroundColor: '#E8E9E9',
          textAlign: 'center',
        },
      },
      muiTableBodyCellProps: {
        ...(column.muiTableBodyCellProps ?? {}),
        align: 'center',
        sx: {
          ...(column.muiTableBodyCellProps?.sx ?? {}),
          color: '#667085',
          fontSize: '12px',
          textAlign: 'center',
        },
      },
    }));
  }, []);
  const table = useMaterialReactTable({
    columns,
    data: cleanedData,
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableGrouping: true,
    enableRowSelection: false,
    enableColumnPinning: true,
    enableFacetedValues: true,
    paginationDisplayMode: 'pages',
    positionToolbarAlertBanner: 'bottom',
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
    },
    muiPaginationProps: {
      color: 'secondary',
      rowsPerPageOptions: [10, 20, 30],
      shape: 'rounded',
      variant: 'outlined',
      sx: {
        '& .MuiPaginationItem-root.Mui-selected': {
          backgroundColor: '#BD1D23',
          color: 'white',
        },
      },
    },
    enableBottomToolbar: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enablePagination: true,
    muiTableContainerProps: { sx: { maxHeight: 'calc(100vh - 320px)' } },
    muiTableBodyCellProps: {
      align: 'center',
      sx: {
        color: '#667085',
        backgroundColor: '#fff',
        fontSize: '12px',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: '125%',
        height: '36px',
        textAlign: 'center',
      },
    },
    muiTableHeadCellProps: {
      align: 'center',
      sx: {
        color: '#344054',
        backgroundColor: '#E8E9E9',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: '600',
        lineHeight: '125%',
        textAlign: 'center',
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: 'none',
      },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Tooltip title="Download">
        <svg
          style={{ cursor: 'pointer' }}
          onClick={downloadFile}
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
            stroke="#0000008a"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M7 10L12 15L17 10"
            stroke="#0000008a"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M12 15V3"
            stroke="#0000008a"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </Tooltip>
    ),
  });

  const downloadFile = () => {
    downloadExcelSheetFunction(data, 'inventory');
  };

  return (
    <div style={{ marginTop: '20px', width: '100%', height: '100%' }}>
      <div className="box" style={{ marginTop: '70px' }}>
        <MaterialReactTable table={table} />
      </div>
    </div>
  );
}
